import { Alert, AlertTitle, Box, Link } from "@mui/material";
import { useEffect, useState } from "react";
import {
	fetchJobylonRolesWithoutCv,
	type JobylonRoleWithoutCv,
} from "../api/assignments/jobylon-config";
import { formatDateTime } from "../utils/date-formatting";
import { SnoozeButton, useBannerSnoozed } from "./banner-snooze";

/** How often the banner asks again: a fixed job clears with its next applicant. */
const REFRESH_MS = 5 * 60 * 1000;

/**
 * Jobylon's own steps for sending applicants to a partner on a job —
 * support.jobylon.com/en/articles/176040 — with Artemis as the partner. Jobylon
 * (Nicole Thorson, 2026-09-22): the integration is on for every job, but only
 * a pipeline action sends the applicants, and templates carry it to new jobs.
 */
const FIX_STEPS = [
	"Click Pipeline Actions (the gear).",
	"On the New stage, click Create action.",
	"Choose the action Partner integration.",
	"Tick every candidate type, choose Artemis as the partner, and save.",
];

const JOBYLON_GUIDE_URL =
	"https://support.jobylon.com/en/articles/176040-hubert-integration";

/**
 * Shown to every user of a customer whose latest applicant on an open Jobylon
 * role came without a CV: that job lacks the "Partner integration" pipeline
 * action, so Artemis assesses its applicants without their CV.
 */
export const JobylonCvBanner = () => {
	const [roles, setRoles] = useState<JobylonRoleWithoutCv[]>([]);

	useEffect(() => {
		let mounted = true;
		const load = () => {
			fetchJobylonRolesWithoutCv()
				.then((next) => {
					if (mounted) setRoles(next);
				})
				.catch(() => {});
		};
		load();
		const id = window.setInterval(load, REFRESH_MS);
		return () => {
			mounted = false;
			window.clearInterval(id);
		};
	}, []);

	// Per set of roles: one more role without CVs shows the banner again.
	const bannerKey = `jobylon-missing-cv:${roles
		.map((role) => role.job_id)
		.sort((a, b) => a - b)
		.join(",")}`;
	const snoozed = useBannerSnoozed(bannerKey);

	if (roles.length === 0 || snoozed) return null;

	return (
		<Box data-component="jobylon-cv-banner">
			<Alert
				severity="error"
				data-component="jobylon-cv-alert"
				action={<SnoozeButton bannerKey={bannerKey} />}
			>
				<AlertTitle>
					{roles.length} of your Jobylon jobs{" "}
					{roles.length === 1 ? "isn't" : "aren't"} sending CVs to Artemis
				</AlertTitle>
				Your latest applicants on {roles.length === 1 ? "it" : "them"} arrived
				without a CV, so Artemis is assessing them without one:
				<Box component="ul" sx={{ my: 0.5, pl: 3 }}>
					{roles.map((role) => (
						<li key={role.job_id}>
							{role.assignment_id === null ? (
								<span data-component="jobylon-cv-role">
									{role.title}
								</span>
							) : (
								<Link
									href={`/funnel/assignment/${role.assignment_id}`}
									data-component="jobylon-cv-role"
								>
									{role.title}
								</Link>
							)}{" "}
							— latest application{" "}
							{formatDateTime(role.latest_application)}
						</li>
					))}
				</Box>
				To fix it, open {roles.length === 1 ? "the job" : "each job"} in
				Jobylon:
				<Box component="ol" sx={{ my: 0.5, pl: 3 }}>
					{FIX_STEPS.map((step) => (
						<li key={step}>{step}</li>
					))}
				</Box>
				To make new jobs send CVs too, add the same action to your job
				templates, or ask your Jobylon administrator to. This message goes away
				once the next applicant arrives with a CV.{" "}
				<Link
					href={JOBYLON_GUIDE_URL}
					target="_blank"
					rel="noopener"
					data-component="jobylon-cv-guide"
				>
					Jobylon's guide
				</Link>
			</Alert>
		</Box>
	);
};
